/**
 * DevicesPage — single inventory view across every vendor.
 *
 * Refactored to use the shared <DataTable> primitive (extracted from the
 * site dashboard work). The page now owns: hero + KPIs + filter chips +
 * fixture-warning banner; the table itself (pagination + sort + free-text
 * search) is handled by DataTable.
 */
import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowDownToLine,
  KeyRound,
  RefreshCw,
  Server,
  Stethoscope,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { KpiCard } from "@/components/ui/kpi-card";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import {
  PanelSkeleton,
  Skeleton,
  TableSkeleton,
} from "@/components/ui/skeleton";
import { useInventory } from "@/stores/inventory";
import { useAiAgent } from "@/stores/ai-agent";
import { useAuth } from "@/stores/auth";
import type {
  DeviceStatus,
  DeviceVendor,
  InventoryDevice,
} from "@/api/schemas/devices";
import { cn } from "@/lib/cn";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

/** Build a focused diagnostic prompt for a device. The model's system
 *  prompt teaches it to map symptoms → tools; this just narrows the
 *  scope to one device + its vendor's diagnostic surface. */
function diagnosePrompt(d: InventoryDevice): string {
  const where = d.site ? ` at site "${d.site}"` : "";
  const grp = d.groupOrNetwork
    ? ` (${d.vendor === "meraki" ? "network" : "group"} ${d.groupOrNetwork})`
    : "";
  return (
    `Diagnose ${d.vendor} device ${d.serial}` +
    (d.name ? ` "${d.name}"` : "") +
    where +
    grp +
    `. Current status: ${d.status}. Use the diagnostic tools to check: ` +
    `recent reboots / config changes; port errors and link state; ` +
    `packet loss on uplinks; failed wireless connections (if it's an AP); ` +
    `currently-connected clients. Summarize what's wrong (or "all clear").`
  );
}

const ALL_VENDORS: DeviceVendor[] = ["meraki", "aruba", "shasta"];
const ALL_STATUSES: DeviceStatus[] = [
  "online",
  "alerting",
  "offline",
  "unknown",
];
const DEVICE_SKELETON_COLS = "1.1fr 100px 1.1fr 1.1fr 1fr 150px 180px";
const isInitialLoading = (status: string, dataLength: number) =>
  status === "loading" && dataLength === 0;

function statusBadge(s: DeviceStatus) {
  switch (s) {
    case "online":
      return (
        <Badge variant="ok" dot>
          online
        </Badge>
      );
    case "alerting":
      return (
        <Badge variant="warn" dot>
          alerting
        </Badge>
      );
    case "offline":
      return (
        <Badge variant="err" dot>
          offline
        </Badge>
      );
    case "unknown":
      return (
        <Badge variant="neutral" dot>
          unknown
        </Badge>
      );
  }
}

/** Map a vendor to its deep-detail entry path. */
function vendorOpenPath(d: InventoryDevice): string {
  switch (d.vendor) {
    case "meraki":
      return "/meraki/update-devices";
    case "aruba":
      return "/aruba/devices";
    case "shasta":
      return "/";
  }
}

/** Numeric ranking for status sort — alerting first, then offline, online, unknown. */
const STATUS_RANK: Record<DeviceStatus, number> = {
  alerting: 0,
  offline: 1,
  online: 2,
  unknown: 3,
};

function buildDeviceColumns({
  diagnose,
}: {
  diagnose: (d: InventoryDevice) => void;
}): DataTableColumn<InventoryDevice>[] {
  return [
    {
      key: "name",
      label: "Name",
      width: "1.1fr",
      sortable: true,
      sortValue: (r) => r.name ?? "",
      render: (r) => (
        <div>
          <div className="truncate text-sm font-semibold text-fg">
            {r.name ?? "(unnamed)"}
          </div>
          <div className="font-mono text-[10.5px] text-fg-mute">
            {r.ipAddress ?? "—"} · {r.mac ?? "—"}
          </div>
        </div>
      ),
    },
    {
      key: "vendor",
      label: "Vendor",
      width: "100px",
      sortable: true,
      sortValue: (r) => r.vendor,
      render: (r) => (
        <Badge
          variant={
            r.vendor === "meraki"
              ? "info"
              : r.vendor === "aruba"
                ? "warn"
                : "neutral"
          }
          size="sm"
        >
          {r.vendor}
        </Badge>
      ),
    },
    {
      key: "serial",
      label: "Serial · model",
      width: "1.1fr",
      sortable: true,
      sortValue: (r) => r.serial,
      render: (r) => (
        <div>
          <div className="font-mono text-[11px] text-fg">{r.serial}</div>
          <div className="font-mono text-[10.5px] text-fg-mute">
            {r.model ?? "—"}
          </div>
        </div>
      ),
    },
    {
      key: "site",
      label: "Site",
      width: "1.1fr",
      sortable: true,
      sortValue: (r) => r.site ?? "",
      render: (r) => (
        <span className="font-mono text-[11px] text-fg-dim">
          {r.site ?? "—"}
        </span>
      ),
    },
    {
      key: "group",
      label: "Group / network",
      width: "1fr",
      render: (r) => (
        <span className="font-mono text-[10.5px] text-fg-dim">
          {r.groupOrNetwork ?? "—"}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      width: "150px",
      sortable: true,
      sortValue: (r) => STATUS_RANK[r.status],
      render: (r) => (
        <div className="flex flex-col gap-1">
          {statusBadge(r.status)}
          {r.healthRatio !== undefined ? (
            <div className="h-1 w-20 overflow-hidden rounded-full bg-line/40">
              <div
                className={cn(
                  "h-full rounded-full",
                  r.healthRatio >= 0.95
                    ? "bg-mint"
                    : r.healthRatio >= 0.6
                      ? "bg-peach"
                      : "bg-rose",
                )}
                style={{ width: `${Math.round(r.healthRatio * 100)}%` }}
              />
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: "actions",
      label: "",
      width: "180px",
      align: "right",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => diagnose(r)}
            title="Ask the agent to diagnose this device"
          >
            <Stethoscope size={11} />
            Diagnose
          </Button>
          <Button asChild size="sm" variant="ghost">
            <Link to={vendorOpenPath(r)}>Open →</Link>
          </Button>
        </div>
      ),
    },
  ];
}

function DeviceFilterChips({
  filters,
  counts,
  toggleVendor,
  toggleStatus,
  resetFilters,
}: {
  filters: { vendors: DeviceVendor[]; statuses: DeviceStatus[] };
  counts: { perVendor: Record<DeviceVendor, number> };
  toggleVendor: (v: DeviceVendor) => void;
  toggleStatus: (s: DeviceStatus) => void;
  resetFilters: () => void;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
            Vendor
          </span>
          {ALL_VENDORS.map((v) => {
            const active = filters.vendors.includes(v);
            return (
              <button
                key={v}
                type="button"
                onClick={() => toggleVendor(v)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold capitalize transition-colors",
                  active
                    ? "border-violet/40 bg-aurora-soft text-violet"
                    : "border-line bg-surface text-fg-mute hover:bg-surface-2 hover:text-fg",
                )}
              >
                {v}
                <span className="font-mono opacity-70">
                  {counts.perVendor[v]}
                </span>
              </button>
            );
          })}
          <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
            Status
          </span>
          {ALL_STATUSES.map((s) => {
            const active = filters.statuses.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold capitalize transition-colors",
                  active
                    ? "border-violet/40 bg-aurora-soft text-violet"
                    : "border-line bg-surface text-fg-mute hover:bg-surface-2 hover:text-fg",
                )}
              >
                {s}
              </button>
            );
          })}
          {filters.vendors.length > 0 || filters.statuses.length > 0 ? (
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto"
              onClick={() => resetFilters()}
            >
              Clear filters
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

export function DevicesPage() {
  const animatedRootRef = useUiAutoAnimate();
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("serial") ?? "";

  const { data, filters, status, error, load, setFilters, resetFilters } =
    useInventory();
  const sendAgent = useAiAgent((s) => s.send);
  const setAgentOpen = useAiAgent((s) => s.setOpen);
  const user = useAuth((s) => s.user);
  // "No keys yet" empty state — when the user hasn't configured a Meraki
  // key, the cross-vendor inventory leg quietly returns no rows. Bland
  // "No devices in inventory" is misleading; surface a real CTA instead.
  // Aruba doesn't have a `configured` flag on the auth shape yet, so we
  // gate purely on Meraki — accurate for the typical onboarding case
  // (users add Meraki first) and harmless for everyone else (the card
  // only appears when both data IS empty AND Meraki isn't configured).
  const showNoKeysEmptyState =
    !!user &&
    user.merakiKeyConfigured === false &&
    data.length === 0 &&
    !error &&
    status === "ready";

  /** Open the agent drawer + fire a focused diagnostic prompt for one
   *  device. Lets operators triage without typing — common ops shortcut. */
  function diagnose(d: InventoryDevice) {
    setAgentOpen(true);
    void sendAgent(diagnosePrompt(d));
  }
  useEffect(() => {
    void load();
  }, [load]);

  function toggleVendor(v: DeviceVendor) {
    const has = filters.vendors.includes(v);
    setFilters({
      vendors: has
        ? filters.vendors.filter((x) => x !== v)
        : [...filters.vendors, v],
    });
  }
  function toggleStatus(s: DeviceStatus) {
    const has = filters.statuses.includes(s);
    setFilters({
      statuses: has
        ? filters.statuses.filter((x) => x !== s)
        : [...filters.statuses, s],
    });
  }

  // Apply chip filters at this level — DataTable handles free-text search separately.
  const visible = useMemo(() => {
    const vendorSet = new Set(filters.vendors);
    const statusSet = new Set(filters.statuses);
    return data.filter((d) => {
      if (filters.vendors.length > 0 && !vendorSet.has(d.vendor))
        return false;
      if (filters.statuses.length > 0 && !statusSet.has(d.status))
        return false;
      return true;
    });
  }, [data, filters]);

  // Headline counters — always compute from `data`, not `visible`.
  const counts = useMemo(() => {
    const c = {
      total: data.length,
      online: 0,
      alerting: 0,
      offline: 0,
      perVendor: { meraki: 0, aruba: 0, shasta: 0 } as Record<
        DeviceVendor,
        number
      >,
    };
    for (const d of data) {
      if (d.status === "online") c.online++;
      if (d.status === "alerting") c.alerting++;
      if (d.status === "offline") c.offline++;
      c.perVendor[d.vendor]++;
    }
    return c;
  }, [data]);

  const cols = buildDeviceColumns({ diagnose });
  const totalSuffixEl = `of ${counts.total} total devices`;

  if (isInitialLoading(status, data.length)) {
    return (
      <div ref={animatedRootRef} className="flex flex-col gap-6">
        <div className="flex items-center gap-2 text-xs text-fg-mute">
          <Skeleton className="h-3 w-20" />
          <span className="opacity-40">/</span>
          <Skeleton className="h-3 w-14" />
        </div>

        <div className="flex items-end justify-between gap-6">
          <div className="flex max-w-[680px] flex-1 flex-col gap-2">
            <Skeleton className="h-10 w-40" />
            <Skeleton className="h-4 w-[92%]" />
            <Skeleton className="h-4 w-[84%]" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-28 rounded-lg" />
            <Skeleton className="h-9 w-24 rounded-lg" />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-4">
          <PanelSkeleton />
          <PanelSkeleton />
          <PanelSkeleton />
          <PanelSkeleton />
        </div>

        <Card>
          <CardContent className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Skeleton className="h-3 w-10" />
              <Skeleton className="h-7 w-20 rounded-full" />
              <Skeleton className="h-7 w-20 rounded-full" />
              <Skeleton className="h-3 w-12" />
              <Skeleton className="h-7 w-20 rounded-full" />
              <Skeleton className="h-7 w-20 rounded-full" />
              <Skeleton className="ml-auto h-8 w-28 rounded-lg" />
            </div>
          </CardContent>
        </Card>

        <TableSkeleton
          columns={DEVICE_SKELETON_COLS}
          columnCount={7}
          rows={8}
        />
      </div>
    );
  }

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Devices</span>
      </div>

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            Devices
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Cross-vendor inventory — every device Opus can reach across Meraki,
            Aruba, and Shasta. Click any row to open the vendor-specific edit
            surface.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost">
            <ArrowDownToLine size={13} />
            Export CSV
          </Button>
          <Button
            size="sm"
            onClick={() => void load()}
            disabled={status === "loading"}
          >
            <RefreshCw size={13} />
            Refresh
          </Button>
        </div>
      </div>

      {error ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach"
        >
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Showing fixture data.</b>{" "}
            <span className="text-peach/80">{error}</span>
          </span>
        </output>
      ) : null}

      {/* KPIs — always reflect the full estate, ignore filter chips. */}
      <div className="grid grid-cols-4 gap-3.5">
        <KpiCard
          label="Total devices"
          value={counts.total}
          icon={<Server size={14} />}
          trend="flat"
          trendLabel="across all vendors"
        />
        <KpiCard
          label="Online"
          value={counts.online}
          trend="up"
          trendLabel={`${Math.round((counts.online / Math.max(counts.total, 1)) * 100)}% of fleet`}
        />
        <KpiCard
          label="Alerting"
          value={counts.alerting}
          trend={counts.alerting > 0 ? "down" : "flat"}
          trendLabel={counts.alerting > 0 ? "needs attention" : "clean"}
        />
        <KpiCard
          label="Offline"
          value={counts.offline}
          trend={counts.offline > 0 ? "down" : "flat"}
          trendLabel={
            counts.offline > 0 ? "see status column" : "all reachable"
          }
        />
      </div>

      {/* Filter chips — apply outside DataTable so the table sees pre-filtered rows. */}
      <DeviceFilterChips
        filters={filters}
        counts={counts}
        toggleVendor={toggleVendor}
        toggleStatus={toggleStatus}
        resetFilters={resetFilters}
      />

      {/* No-keys empty state — friendlier than "No devices in inventory"
          when the real reason is "you haven't added a vendor key yet." */}
      {showNoKeysEmptyState ? (
        <Card>
          <CardContent className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-aurora-soft text-violet">
              <KeyRound size={20} />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-fg">No vendor keys yet</div>
              <div className="text-sm text-fg-dim">
                Opus calls every vendor with your credentials. Add a Meraki key
                or an Aruba bundle to populate this list.
              </div>
            </div>
            <div className="flex gap-2">
              <Button asChild size="sm" variant="ghost">
                <Link to="/aruba/keys">
                  <KeyRound size={13} />
                  Aruba bundle
                </Link>
              </Button>
              <Button asChild size="sm" variant="primary">
                <Link to="/meraki/keys">
                  <KeyRound size={13} />
                  Meraki key
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* The actual table — DataTable owns search, sort, pagination. */}
      <DataTable
        key={initialQuery}
        columns={cols}
        data={visible}
        rowKey={(r) => `${r.vendor}-${r.serial}`}
        searchPlaceholder="Search by name, serial, model, MAC, IP, site, group…"
        initialQuery={initialQuery}
        searchableFields={
          [
            "name",
            "serial",
            "model",
            "site",
            "groupOrNetwork",
            "ipAddress",
            "mac",
          ] as Array<keyof InventoryDevice>
        }
        totalSuffix={totalSuffixEl}
        emptyMessage={
          counts.total === 0
            ? "No devices in inventory."
            : "No devices match your filters. Try widening or clearing them."
        }
      />
    </div>
  );
}
